// about vue-router
// MIT License
// 
// Copyright (c) 2013-present Evan You
// 
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
// 
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
// 
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import { EXECUTE_NAVIGATION_RESOURCES } from '../constants/navigationResources';
import { routes } from './routingInfo';
import { headerTitleResources } from './headerTitle';
import { keepSpecialFunctionList } from './keepSpecialFunctionList';

import store from '@/store';
import util from '@/share/util/utils';

import { createRouter, createWebHistory } from 'vue-router';
import Auth from '@/store/modules/auth';

// フローナビゲーションバーの表示種別
// ナビゲーションバーの表示なし
// const flowNavigation_none = 'none';
// ナビゲーションバー通常表示
// const flowNavigation_nomal = 'nomal';

// popstate判定の対象画面
const POPSTATE_TARGET_ROUTE_NAMES = [
  'allCustomize',
  'individualActiveTest',
  'individualCancelCode',
  'individualCustomize',
  'individualDataMonitor',
  'individualDiagCode',
  'individualVehicleControlHistory',
  'individualWorkSupport',
  'multipleDataMonitor',
  'spCancelCode',
  'spDataMonitor',
  'spDiagCode',
  'spVehicleControlHistory',
  'spWorkSupport',
  'termsAgreement',
];

// ブラウザ戻るボタン検知
// createRouter() より前に登録することで Vue Router の popstate リスナーより先に発火させる
// afterEach で設定された isDisableBackClicks フラグを見て、抑制エントリか実ナビゲーションかを判定
let _pendingPopstate = false;

window.addEventListener('popstate', (event) => {
  // afterEach で立てた isDisableBackClicks フラグを確認
  // true = ブラウザの戻るボタンによる遷移 → フラグを立てて、次の遷移で判定させる
  // false = Vue Router のナビゲーションによる遷移 → フラグを立てない
  const hasDisableBackClicks = Object.prototype.hasOwnProperty.call(event.state ?? {}, 'isDisableBackClicks');
  _pendingPopstate = hasDisableBackClicks;
}, false);

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: routes,
});

router.beforeEach((to, from, next) => {
  // console.log("from : " + from.name + " to : " + to.name);

  // ブラウザ戻るボタン判定
  // popstate リスナー（createRouter より前に登録済み）で立てたフラグを判定（対象画面のみ有効）
  const isBrowserBack = _pendingPopstate;
  _pendingPopstate = false;
  store.commit('cache/setIsPopstateNavigation', isBrowserBack && POPSTATE_TARGET_ROUTE_NAMES.includes(to.name));

  // 中国開発対応(ドメイン名がないので本処理を記載するとSSM5が動作しなくなるため判定処理追加)
  if (!process.env.VUE_APP_DEV_ENVIRONMENT) {
    if (to.name === 'blank') {
      if (from.name === 'login') {
        // nop ブラウザバック対応 blank 画面は login 画面とパスワード変更画面(new)からのみ許可とする 
      }
      else {
        next({ path: '/' });
        return;
      }
    }
    /**
     * リプロ結果画面でブラウザバックが発生した場合に、リプロ実行画面に戻って
     * 再度 startRepro API が走るケースを回避するための分岐
     */
    else if (to.name === 'execution' && from.name === 'result') {
      next({ path: '/' });
      return;
    }
  }

  // 遷移した画面のタイトルを設定(現時点では使用していない)
  if (to.name) {
    store.dispatch('app/setHeaderTitleID', headerTitleResources[to.name]);
  }

  // システム個別診断の機能選択画面の特殊機能表示状態を解除
  if (!keepSpecialFunctionList.includes(to.name)) {
    store.dispatch('vehicle/setDisplaySpecialFunction', []);
  }

  if (to.matched.some(record => record.meta.requiresAuth) && !Auth.state.loggedIn) {
    next({ path: '/' });
  } else {

    if (to.meta) {
      store.dispatch('app/setMainContainerStyle', to.meta.containerStyle);
      store.dispatch('app/setHeaderType', to.meta.headerType);
      store.dispatch('app/setFlowNavigationType', to.meta.flowNavigationType);
      store.dispatch('app/setViewProcessMode', to.meta.viewProcessMode);

      // パンくず設定の実施
      if (to.meta.breadCrumbLevel) {
        let data;
        if (typeof to.meta.breadCrumbData === "function") {
          // マッピングを利用せず、動的にデータを作る場合
          data = to.meta.breadCrumbData(to, from);
        } else {
          // マッピングを利用する場合
          data = util.createBreadCrumbData(to, EXECUTE_NAVIGATION_RESOURCES);
        }
        store.dispatch('app/setBreadCrumbs', data);
      }
    }

    next();
  }
});

/**
 * ページ遷移後のローディング処理
 * ※ブラウザの戻るボタンの制御（想定回数分戻るボタンを無効化）
 * @param {object} to 遷移後の情報
 * @param {object} from 遷移前の情報
 */
router.afterEach((to, from) => {
  // 同じ画面への遷移を防ぐ（戻るボタンによる遷移を止める）
  if (to.fullPath === from.fullPath) {
    return;
  }

  // 遷移後情報からURLを生成
  let url;
  try {
    const path = to.fullPath || to.path || window.location.pathname;
    url = new URL(path, window.location.origin).toString();
  } catch (e) {
    // URLの生成に失敗した場合は、pushStateを行わない
    console.warn('[router.afterEach] invalid url. skip pushState.', e);
    return;
  }

  // 戻るボタンを無効化する回数
  const maxDisableBackClicks = 4; 

  // pushStateを使用して、ブラウザの戻るボタンを無効化する履歴を追加
  try {
    const baseState = history.state ?? {};

    // 5回目: 戻るボタン有効化(古い順に格納)
    history.pushState({ ...baseState, isDisableBackClicks: false }, '', url);
    // 1〜4回目: 戻るボタン無効化（現在の画面の履歴も作成するため、＋１ループとしている）
    for (let i = 0; i < maxDisableBackClicks + 1; i++) {
      history.pushState({ ...baseState, isDisableBackClicks: true }, '', url);
    }
  } catch (e) {
    console.warn('[router.afterEach] pushState failed. skip.', e);
  }
});

export default router;
